class SingleNode<T>  {

    index: number = -1;
    value: T;
    next: SingleNode<T> | undefined;

    constructor(value: T) {
        this.value = value;
    }

    equals(value: T | SingleNode<T>) {
        if (value instanceof SingleNode)
            return this.value == value.value;
        return this.value == value;
    }

}

class DoubleNode<T> extends SingleNode<T> {

    next: DoubleNode<T> | undefined;
    prev: DoubleNode<T> | undefined;

    constructor(value: T) {
        super(value);
    }

}

export class LinkedList<T> {

    headNode: DoubleNode<T> | undefined;
    tailNode: DoubleNode<T> | undefined;
    _size: number = 0;

    constructor(...values: T[]) {
        if (values.length > 0) {
            values.forEach(v => this.append(v));
        }
    }

    get head() {
        return this.headNode?.value;
    }

    get tail() {
        return this.tailNode?.value;
    }

    get size() {
        return this._size;
    }

    *iterator(): IterableIterator<T> {
        let current = this.headNode;
        while (current) {
            yield current.value
            current = current.next
        }
    }

    [Symbol.iterator]() {
        return this.iterator();
    }

    isEmpty(): boolean {
        return !this.headNode;
    }

    append(value: T, checkDuplicates: boolean = false): boolean {

        if (checkDuplicates && this.isDuplicate(value)) {
            return false;
        }

        const newNode = new DoubleNode<T>(value);

        if (!this.tailNode) {
            this.headNode = this.tailNode = newNode;
        } else {
            this.tailNode.next = newNode;
            newNode.prev = this.tailNode;
            this.tailNode = newNode;
        }

        this._size++;
        this.reIndex();
        return true;
    }

    prepend(value: T, checkDuplicates: boolean = false): boolean {

        if (checkDuplicates && this.isDuplicate(value)) {
            return false;
        }

        let newNode = new DoubleNode<T>(value);

        if (!this.headNode) {
            this.headNode = this.tailNode = newNode;
        } else {
            newNode.next = this.headNode;
            this.headNode.prev = newNode;
            this.headNode = newNode;
        }

        this._size++;
        this.reIndex();
        return true;
    }

    private insert(value: T, otherValue: T, before: boolean = false, checkDuplicates: boolean = false): boolean {

        if (this.isEmpty())
            return false;

        if (checkDuplicates && this.isDuplicate(value)) {
            return false;
        }

        let newNode = new DoubleNode(value);
        let currentNode = this.headNode;

        while (true) {
            if (!currentNode) {
                return false;

            } else if (currentNode.value === otherValue) {

                if (before) {
                    newNode.prev = currentNode.prev;
                    newNode.next = currentNode;
                    currentNode.prev = newNode;
                    if (newNode.prev) {
                        newNode.prev.next = newNode;
                    } else {
                        this.headNode = newNode;
                    }
                } else {
                    newNode.prev = currentNode.prev;
                    newNode.next = currentNode;
                    currentNode.prev = newNode;

                    if (newNode.prev) {
                        newNode.prev.next = newNode;
                    } else {
                        this.headNode = newNode;
                    }
                }

                this._size++;
                this.reIndex();
                return true;

            } else {
                currentNode = currentNode?.next;
            }
        }

    }

    insertBefore(value: T, nextValue: T, checkDuplicates: boolean = false): boolean {
        return this.insert(value, nextValue, true, checkDuplicates);
    }

    insertAfter(value: T, previousValue: T, checkDuplicates: boolean = false): boolean {
        return this.insert(value, previousValue, true, checkDuplicates);
    }

    remove(value: T): T | undefined {
        let currentNode = this.headNode;

        if (!currentNode) { //isEmpty but we don't use the method to avoid typescrip warnings
            return;

        } else if (currentNode.value === value) {
            this.headNode = currentNode.next;
            if (this.headNode) this.headNode.prev = undefined;
            currentNode.next = currentNode.prev = undefined;
            this._size--;
            this.reIndex();
            return currentNode.value;

        } else {
            while (true) {
                if (!currentNode) {
                    return;

                } else if (currentNode.value === value) {

                    if (currentNode.next) { // special case for last element
                        if (currentNode.prev) currentNode.prev.next = currentNode.next;
                        currentNode.next.prev = currentNode.prev;
                        currentNode.next = currentNode.prev = undefined;

                    } else {
                        if (currentNode.prev) currentNode.prev.next = undefined;
                        this.tailNode = currentNode.prev;
                        currentNode.next = currentNode.prev = undefined;
                    }

                    this._size--;
                    this.reIndex();
                    return currentNode.value;

                } else {
                    currentNode = currentNode.next;
                }
            }
        }
    }

    removeHead(): T | undefined {
        let currentNode = this.headNode;

        if (!currentNode) {
            return;
        }

        // single item list
        if (!this.headNode?.next) {
            this.headNode = undefined;
            this.tailNode = undefined;

            // full list
        } else {
            this.headNode.next.prev = undefined;
            this.headNode = this.headNode.next;
            currentNode.next = currentNode.prev = undefined;
        }

        this._size--;
        this.reIndex();
        return currentNode.value;
    }

    removeTail(): T | undefined {

        let currentNode = this.tailNode;

        if (!currentNode) {
            return;
        }

        // single item list
        if (!this.tailNode?.prev) {
            this.headNode = undefined;
            this.tailNode = undefined;

            // full list
        } else {
            this.tailNode.prev.next = undefined;
            this.tailNode = this.tailNode.prev;
            currentNode.next = currentNode.prev = undefined;
        }

        this._size--;
        this.reIndex();
        return currentNode.value;
    }

    first(count: number): T[] {
        let iter = this.iterator();
        let result: any[] = [];

        let n = Math.min(count, this.size);

        for (let i = 0; i < n; i++) {
            let node = iter.next();
            result.push(node.value);
        }

        return result;
    }

    toArray(): T[] {
        return [...this];
    }

    isDuplicate(value: T): boolean {
        let set = new Set(this.toArray()); //-- optimzie for memory by storing the array and updating it?
        return set.has(value);
    }

    private reIndex() {
        let node = this.headNode,
            index = 0;
        while (node) {
            node.index = index++;
            node = node.next;
        }
    }

    clear() {
        this.headNode = undefined;
        this.tailNode = undefined;
        this._size = 0;
    }

}

export class Stack<T>  {

    protected linkedList: LinkedList<T> = new LinkedList();

    enqueue(value: T): void {
        this.linkedList.prepend(value);
    }

    enqueueBottom(value: T): void {
        this.linkedList.append(value);
    }

    dequeue(value?: T): T | undefined {
        return value ? this.linkedList.remove(value) : this.linkedList.removeHead();
    }

    peek(): T | undefined {
        return this.linkedList.head;
    }

    peekBottom(): T | undefined {
        return this.linkedList.tail;
    }

    isEmpty(): boolean {
        return this.linkedList.isEmpty();
    }

    *iterator(): IterableIterator<T> {
        let current = this.linkedList.headNode;
        while (current) {
            yield current.value
            current = current.next
        }
    }

    [Symbol.iterator]() {
        return this.iterator();
    }

    insertBefore(value: T, before: T): boolean {
        return this.linkedList.insertBefore(value, before);
    }

    insertAfter(value: T, after: T): boolean {
        return this.linkedList.insertAfter(value, after);
    }

    size(): number {
        return this.linkedList.size;
    }

    contains(value: T): boolean {
        return this.linkedList.isDuplicate(value);
    }

    clear() {
        return this.linkedList.clear();
    }

}

export class ShuffleStack<T> extends Stack<T> {

    enqueue(value: T): void {
        if (this.linkedList.head == value)
            return;
        if (this.contains(value))
            this.linkedList.remove(value);
        this.linkedList.prepend(value);
    }

    enqueueBottom(value: T): void {
        if (this.linkedList.tail == value)
            return;
        if (this.contains(value))
            this.linkedList.remove(value);
        this.linkedList.append(value);
    }

    insertBefore(value: T, before: T) {
        if (this.contains(value))
            this.linkedList.remove(value);
        return this.linkedList.insertBefore(value, before);
    }

    insertAfter(value: T, after: T) {
        if (this.contains(value))
            this.linkedList.remove(value);
        return this.linkedList.insertAfter(value, after);
    }

}
